import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { ProjetsService } from 'app/services/projets/projets.service';
import { AwscontrolService } from 'app/services/aws/awscontrol.service';
import Swal from 'sweetalert2';

export interface ProjectRequestData {
  agencyName: string;
  // Prefilled project fields
  name?: string;
  description?: string;
  adresse?: string;
  videoUrlProjet?: string;
  categoryList?: any[];
  promoTitle?: string;
  promoImg?: string;
  nomCommercial?: string;
  numCommercial?: string;
  emailCommercial?: string;
  projetLink?: string;
  status?: string;
  date?: string;
  displayOrder?: number;
  // Existing image URLs from the request
  existingImageUrls?: string[];
}

@Component({
  selector: 'app-create-projet-modal',

  templateUrl: './create-projet-modal.component.html',
  styleUrls: ['./create-projet-modal.component.scss']
})
export class CreateProjetModalComponent {
  projetForm: FormGroup;
  loading = false;
  error = '';

  // Photo management
  existingImages: { url: string; fileName: string }[] = [];
  uploadedFiles: File[] = [];
  uploadedPreviews: string[] = [];
  uploadingImages = false;

  constructor(
    private fb: FormBuilder,
    private projetsService: ProjetsService,
    private awsService: AwscontrolService,
    public dialogRef: MatDialogRef<CreateProjetModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ProjectRequestData
  ) {
    // Initialize form with prefilled values from request
    this.projetForm = this.fb.group({
      name: [data.name || '', Validators.required],
      description: [data.description || ''],
      status: [data.status || 'active'],
      videoUrlProjet: [data.videoUrlProjet || ''],
      adresse: [data.adresse || '', Validators.required],
      photos: [[]],              // will hold uploaded image URLs after upload
      date: [data.date || ''],
      promoTitle: [data.promoTitle || ''],
      promoImg: [data.promoImg || ''],
      categoryList: [data.categoryList || []],
      nomCommercial: [data.nomCommercial || ''],
      numCommercial: [data.numCommercial || ''],
      emailCommercial: [data.emailCommercial || '', Validators.email],
      projetLink: [data.projetLink || ''],
      displayOrder: [data.displayOrder || 0]
    });

    // Prepare existing images list
    if (data.existingImageUrls?.length) {
      this.existingImages = data.existingImageUrls.map(url => ({
        url,
        fileName: this.extractFileName(url)
      }));
    }
  }

  private extractFileName(url: string): string {
    return url.substring(url.lastIndexOf('/') + 1);
  }

  // Download an existing image (without deleting)
  downloadOriginalImage(url: string, fileName: string): void {
    this.awsService.downloadFile(url).subscribe({
      next: (blob) => {
        const link = document.createElement('a');
        const objectUrl = URL.createObjectURL(blob);
        link.href = objectUrl;
        link.download = fileName;
        link.click();
        URL.revokeObjectURL(objectUrl);
        Swal.fire('Succès', `Image "${fileName}" téléchargée`, 'success');
      },
      error: () => Swal.fire('Erreur', 'Impossible de télécharger l\'image', 'error')
    });
  }

  // Handle file selection for new (retouched) images
  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      const files = Array.from(input.files);
      this.uploadedFiles.push(...files);
      // Generate previews
      files.forEach(file => {
        const reader = new FileReader();
        reader.onload = (e) => this.uploadedPreviews.push(e.target?.result as string);
        reader.readAsDataURL(file);
      });
    }
  }

  removeUploadedFile(index: number): void {
    this.uploadedFiles.splice(index, 1);
    this.uploadedPreviews.splice(index, 1);
  }

  private async uploadNewImages(): Promise<string[]> {
    if (this.uploadedFiles.length === 0) return [];
    const uploadPromises = this.uploadedFiles.map(file => this.awsService.uploadFile(file).toPromise());
    const results = await Promise.all(uploadPromises);
    // Fix: use the correct property name – your service returns { urls: string[] }
    return results.flatMap(res => res?.urls || []);
  }

  async onSubmit(): Promise<void> {
    if (this.projetForm.invalid) {
      this.projetForm.markAllAsTouched();
      return;
    }

    this.loading = true;
    this.error = '';

    try {
      // 1. Upload new (retouched) images
      const newImageUrls = await this.uploadNewImages();

      // 2. Build final project object
      const projectData = {
        ...this.projetForm.value,
        agencyName: this.data.agencyName,
        photos: newImageUrls          // only the newly uploaded images
      };

      // 3. Create project via backend
      this.projetsService.createProjet(projectData).subscribe({
        next: (created) => {
          this.loading = false;
          this.dialogRef.close(created);
        },
        error: (err) => {
          this.loading = false;
          this.error = 'Erreur lors de la création du projet.';
          console.error(err);
        }
      });
    } catch (err) {
      this.loading = false;
      this.error = 'Erreur lors de l\'upload des images.';
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}